/**
 * Reading receipts on the device: runs the OCR engine on a stored image, parses the text, matches
 * it against the user's own merchants and categories, and stores the result as an `Extraction`
 * (a prediction, separate from the expense). Nothing leaves the device.
 */
import { learnAlias, recognizeReceipt, type MerchantAlias } from "@/domain/merchant-recognition";
import { normalizeMerchant } from "@/domain/merchants";
import type { DraftForm, Expense, Extraction } from "@/domain/model";
import { mergeExtraction } from "@/domain/ocr/merge";
import { parseReceipt } from "@/domain/ocr/receipt-parser";
import type { OcrEngine } from "@/integrations/ocr/tesseract";
import { newId } from "@/lib/hash";
import { nowIso, type BudgetDatabase } from "./db";

// ─── Running reads (one per image at a time), with progress for the UI ─────────────────────────

const running = new Map<string, { promise: Promise<Extraction | null>; progress: number }>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** For `useSyncExternalStore`. */
export function subscribeReading(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 0–1 while the image is being read, null otherwise. */
export function readingProgress(attachmentId: string | null | undefined): number | null {
  return attachmentId ? (running.get(attachmentId)?.progress ?? null) : null;
}

export async function getExtraction(db: BudgetDatabase, attachmentId: string): Promise<Extraction | null> {
  return (await db.extractions.where("attachmentId").equals(attachmentId).last()) ?? null;
}

async function recognitionContext(db: BudgetDatabase, today: string) {
  const [aliases, expenses, categories, methods] = await Promise.all([
    db.merchantAliases.toArray(),
    db.expenses.filter((e) => e.status === "confirmed" && e.merchant !== null).toArray(),
    db.categories.toArray(),
    db.paymentMethods.toArray(),
  ]);
  return {
    aliases: new Map(aliases.map((a) => [a.id, a])),
    expenses: expenses.map((e) => ({ merchant: e.merchant, categoryId: e.categoryId, purchaseDate: e.purchaseDate, updatedAt: e.updatedAt })),
    activeCategoryIds: new Set(categories.filter((c) => c.isActive).map((c) => c.id)),
    activePaymentMethodIds: new Set(methods.filter((m) => m.isActive).map((m) => m.id)),
    today,
  };
}

async function read(db: BudgetDatabase, attachmentId: string, engine: OcrEngine, today: string, force: boolean): Promise<Extraction | null> {
  const attachment = await db.attachments.get(attachmentId);
  if (!attachment) return null;
  const previous = await getExtraction(db, attachmentId);
  // Idempotent: the same image is read once, unless the user asks to read it again.
  if (!force && previous && previous.attachmentSha256 === attachment.sha256) return previous;

  const started = Date.now();
  const now = () => nowIso();
  let record: Extraction;
  try {
    const ocr = await engine(new Blob([attachment.data], { type: attachment.mimeType }), (p) => {
      const job = running.get(attachmentId);
      if (job) {
        job.progress = p;
        notify();
      }
    });
    const reading = parseReceipt(ocr.text, ocr.lines, { today });
    const suggestion = recognizeReceipt(reading, await recognitionContext(db, today));
    record = {
      id: newId(),
      attachmentId,
      attachmentSha256: attachment.sha256,
      engine: ocr.engine,
      status: "done",
      text: ocr.text,
      lines: ocr.lines,
      reading,
      suggestion,
      error: null,
      durationMs: Date.now() - started,
      applied: {},
      appliedAt: null,
      final: null,
      reviewedAt: null,
      createdAt: now(),
      updatedAt: now(),
    };
  } catch (error) {
    record = {
      id: newId(),
      attachmentId,
      attachmentSha256: attachment.sha256,
      engine: "unknown",
      status: "failed",
      text: "",
      lines: [],
      reading: null,
      suggestion: null,
      error: (error as Error).message,
      durationMs: Date.now() - started,
      applied: {},
      appliedAt: null,
      final: null,
      reviewedAt: null,
      createdAt: now(),
      updatedAt: now(),
    };
  }
  return db.transaction("rw", [db.attachments, db.extractions], async () => {
    // The image may have been replaced or removed while it was being read: drop the result.
    const current = await db.attachments.get(attachmentId);
    if (!current || current.sha256 !== record.attachmentSha256) return null;
    await db.extractions.where("attachmentId").equals(attachmentId).delete();
    await db.extractions.add(record);
    return record;
  });
}

/**
 * Reads an image (or returns the stored reading). Concurrent calls for the same image share one
 * run. A failure is stored as a `failed` extraction and never blocks saving the expense.
 */
export function readImage(db: BudgetDatabase, attachmentId: string, engine: OcrEngine, { today, force = false }: { today: string; force?: boolean }): Promise<Extraction | null> {
  const existing = running.get(attachmentId);
  if (existing) return existing.promise;
  const promise = read(db, attachmentId, engine, today, force).finally(() => {
    running.delete(attachmentId);
    notify();
  });
  running.set(attachmentId, { promise, progress: 0 });
  notify();
  return promise;
}

/**
 * Fills a draft's empty fields from a reading, once (see `mergeExtraction`). `defaults` is the
 * form a new expense starts with, used when the draft has no saved form yet.
 */
export async function applyExtractionToDraft(db: BudgetDatabase, draftId: string, extractionId: string, defaults: DraftForm): Promise<boolean> {
  return db.transaction("rw", [db.expenseDrafts, db.extractions], async () => {
    const extraction = await db.extractions.get(extractionId);
    const draft = await db.expenseDrafts.get(draftId);
    if (!extraction || !draft || extraction.status !== "done" || extraction.appliedAt) return false;
    const now = nowIso();
    const { form, applied } = mergeExtraction(draft.form ?? defaults, extraction.reading, extraction.suggestion, { purchaseDate: defaults.purchaseDate });
    await db.expenseDrafts.update(draftId, { form, updatedAt: now });
    await db.extractions.update(extractionId, { applied, appliedAt: now, updatedAt: now });
    return true;
  });
}

/** The category source of an expense saved from a reading: did the user keep the suggestion? */
export function categorySourceFor(extraction: Extraction | null, categoryId: string): Expense["categorySource"] {
  const suggested = extraction?.suggestion?.category;
  return suggested && suggested.categoryId === categoryId ? suggested.source : "user";
}

/**
 * Called by `saveExpense` inside its transaction when a draft with an image becomes an expense:
 * records what the user saved and learns aliases / merchant rules from their corrections.
 */
export async function learnFromSave(db: BudgetDatabase, extraction: Extraction, expense: Expense): Promise<void> {
  const now = nowIso();
  await db.extractions.update(extraction.id, {
    final: { amount: expense.amount, purchaseDate: expense.purchaseDate, merchant: expense.merchant, categoryId: expense.categoryId, paymentMethodId: expense.paymentMethodId },
    reviewedAt: now,
    updatedAt: now,
  });
  const ocrMerchant = extraction.reading?.merchant?.value ?? null;
  if (!ocrMerchant) return;
  const key = normalizeMerchant(ocrMerchant);
  const existing: MerchantAlias | undefined = key ? await db.merchantAliases.get(key) : undefined;
  const alias = learnAlias(ocrMerchant, extraction.suggestion, { merchant: expense.merchant, categoryId: expense.categoryId }, existing, now);
  if (alias) await db.merchantAliases.put(alias);
}
